/**
 * Profile Page Constants
 * Level icons, XP values, and configuration
 */

import type { LevelIconType, Badge } from '../types';

// ============================================================================
// Level Icon Mapping
// ============================================================================

export const LEVEL_ICONS: Record<number, LevelIconType> = {
  1: 'medal',
  2: 'medal',
  3: 'award',
  4: 'award',
  5: 'star',
  6: 'star',
  7: 'trophy',
  8: 'trophy',
  9: 'diamond',
  10: 'diamond',
};

// ============================================================================
// XP Values
// ============================================================================

export const XP_VALUES = {
  AS_IS_SCAN: 5,
  REFERRAL: 500,
  SHARE_FIRST: 100,
  SHARE_SECOND: 150,
  SHARE_THIRD: 300,
} as const;

// ============================================================================
// Store Preferences Defaults
// ============================================================================

export const STORE_PREFS_DEFAULTS = {
  MAX_DISTANCE_MIN: 25,
  MAX_DISTANCE_MAX: 300,
  MAX_DISTANCE_STEP: 25,
  MAX_DISTANCE_DEFAULT: 100,
  BULK_THRESHOLD_MIN: 5,
  BULK_THRESHOLD_MAX: 50,
  BULK_THRESHOLD_STEP: 5,
  BULK_THRESHOLD_DEFAULT: 15,
} as const;

// ============================================================================
// Badge Icon Mapping
// ============================================================================

export const BADGE_ICON_MAP: Record<string, string> = {
  scan: 'Scan',
  map: 'Target',
  'user-plus': 'Users',
  'person-add': 'Users',
  users: 'Users',
  people: 'Users',
  award: 'Award',
  ribbon: 'Award',
  calendar: 'Flame',
  footsteps: 'Medal',
  footprints: 'Medal',
  search: 'Target',
  tag: 'Gift',
  pricetag: 'Gift',
  star: 'Star',
  trophy: 'Trophy',
  diamond: 'Diamond',
} as const;

// ============================================================================
// All Badge Definitions
// ============================================================================

export const ALL_BADGES: Badge[] = [
  { id: 'first_scan', name: 'First Scan', description: 'Scan your first As-Is item', icon: 'scan' },
  { id: 'store_explorer', name: 'Store Explorer', description: 'Scan at 3 different stores', icon: 'map' },
  { id: 'referral_rookie', name: 'Referral Rookie', description: 'Get your first referral', icon: 'user-plus' },
  { id: 'referral_champion', name: 'Referral Champion', description: 'Get 10 referrals', icon: 'users' },
  { id: 'century_scanner', name: 'Century Scanner', description: 'Scan 100 As-Is items', icon: 'award' },
  { id: 'daily_dedicated', name: 'Daily Dedicated', description: 'Use app 7 days in a row', icon: 'calendar' },
  { id: 'first_steps', name: 'First Steps', description: 'Reach Level 1', icon: 'footsteps' },
  { id: 'deal_seeker', name: 'Deal Seeker', description: 'Reach Level 3', icon: 'search' },
  { id: 'bargain_hunter', name: 'Bargain Hunter', description: 'Reach Level 5', icon: 'tag' },
  { id: 'as_is_expert', name: 'As-Is Expert', description: 'Reach Level 7', icon: 'star' },
  { id: 'ikea_legend', name: 'IKEA Legend', description: 'Reach Level 9', icon: 'trophy' },
  { id: 'grandmaster', name: 'Grandmaster', description: 'Reach Level 10', icon: 'diamond' },
];

// ============================================================================
// Stat Card Colors
// ============================================================================

export const STAT_CARD_COLORS = {
  level: { bg: 'bg-yellow-500/30', icon: 'text-white' },
  xp: { bg: 'bg-purple-500/30', icon: 'text-white' },
  streak: { bg: 'bg-orange-500/30', icon: 'text-white' },
  referrals: { bg: 'bg-blue-500/30', icon: 'text-white' },
} as const;

// ============================================================================
// Stat Card Colors (for content area)
// ============================================================================

export const STAT_ICON_COLORS = {
  scans: { bg: 'bg-blue-100', icon: 'text-blue-600' },
  streak: { bg: 'bg-orange-100', icon: 'text-orange-600' },
  referrals: { bg: 'bg-purple-100', icon: 'text-purple-600' },
  slots: { bg: 'bg-green-100', icon: 'text-green-600' },
} as const;

// ============================================================================
// Earn XP Card Colors
// ============================================================================

export const EARN_XP_COLORS = {
  scan: { bg: 'bg-blue-100', icon: 'text-blue-600' },
  refer: { bg: 'bg-purple-100', icon: 'text-purple-600' },
  share: { bg: 'bg-green-100', icon: 'text-green-600' },
} as const;
